import { trpc } from "@/lib/trpc";
import { COOKIE_NAME, UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import { defineElement } from "@lordicon/element";
import superjson from "superjson";
import App from "./App";
import { getLoginPath } from "./const";
import "./index.css";

// Register the Lordicon web component once so interactive brand accents remain local to page components.
if (!customElements.get("lord-icon")) {
  defineElement();
}

const queryClient = new QueryClient();

const logApiFailure = (operation: "query" | "mutation", error: unknown) => {
  console.error(JSON.stringify({
    event: "browser_api_failure",
    operation,
    outcome: "failed",
    errorType: error instanceof TRPCClientError ? "trpc" : "unexpected",
    code: error instanceof TRPCClientError && typeof error.data?.code === "string"
      ? error.data.code
      : undefined,
  }));
};

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  const currentPath = `${window.location.pathname}${window.location.search}`;
  window.location.assign(getLoginPath(currentPath));
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    logApiFailure("query", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    logApiFailure("mutation", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      headers() {
        const headers: Record<string, string> = {};
        if (typeof window !== "undefined" && window.location.pathname === "/guest-tickets") {
          headers["x-chi-ticket-guest"] = "1";
        }
        // Preview session fallback: when the browser blocks iframe cookies
        // (Safari ITP / private browsing / WebView), the runtime mirrors the
        // session into sessionStorage so we can forward it as a Bearer token.
        // The regular OAuth cookie flow keeps working and takes priority server-side.
        try {
          const raw = sessionStorage.getItem("chi-session-cookie");
          if (raw) {
            const prefix = `${COOKIE_NAME}=`;
            const pair = raw.split(";").find(s => s.trim().startsWith(prefix));
            const token = pair?.trim().slice(prefix.length);
            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch {
          // sessionStorage unavailable
        }
        return headers;
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

let appRendered = false;
function renderApp() {
  if (appRendered) return;
  appRendered = true;
  createRoot(document.getElementById("root")!).render(
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>,
  );
}

const responsiveMediaGlobal = globalThis as typeof globalThis & {
  __RESPONSIVE_MEDIA_INDEX__?: unknown;
};
if (responsiveMediaGlobal.__RESPONSIVE_MEDIA_INDEX__) {
  renderApp();
} else {
  const responsiveMediaIndex = document.createElement("script");
  responsiveMediaIndex.src = "/media/.responsive/index.js";
  responsiveMediaIndex.addEventListener("load", renderApp, { once: true });
  responsiveMediaIndex.addEventListener("error", renderApp, { once: true });
  document.head.append(responsiveMediaIndex);
}
